import {Event} from '../events/Event';
import WebSocketType = require('ws');
import {ConnectionEvent, DisconnectEvent, MessageEvent} from './IConnection';
const PORT = 8160;
const NODE = typeof process === 'object';

export interface HandshakeMessage {
    type: 'handshake';
    status: 'OK';
    name: string;
}

export interface ConnectionSettings {
    hostname: string;
    port?: number;
    key?: string;
    cert?: string;
    fingerprint?: string;
}

export default class ConnectionManager {
    public connected: Event<ConnectionEvent>;
    public disconnected: Event<DisconnectEvent>;
    public message: Event<MessageEvent>;

    private namedConnections: {[key: string]: WebSocket|WebSocketType};

    constructor() {
        this.onConnection = this.onConnection.bind(this);
        this.onClose = this.onClose.bind(this);

        this.namedConnections = {};
        this.connected = new Event('Connected');
        this.disconnected = new Event('Disconnected');
        this.message = new Event('Message Received');
    }

    public connect(robotName:string, options:any):Promise<ConnectionEvent> {
        if (this.isConnected(robotName)) {
            return Promise.resolve({robot:robotName});
        }

        return new Promise((resolve, reject) => {
            this.connected.on(resolve);
            this.startWebSocket({hostname: robotName, ...options}, (err) => {
                this.connected.off(resolve);
                reject(err);
            });
        });
    }

    public close():void {
        for (let robot in this.namedConnections) {
            this.namedConnections[robot].close();
        }
        this.namedConnections = {};
    }

    public isConnected(name: string):boolean {
        return name in this.namedConnections;
    }

    public sendTo(name: string, command: JIBO.v1.Command):void {
        if (!this.isConnected(name)){
            return;
        }

        //don't care about knowing when the send completes
        if (NODE) {
            (this.namedConnections[name] as WebSocketType).send(JSON.stringify(command));
        } else {
            (this.namedConnections[name] as WebSocket).send(JSON.stringify(command));
        }
    }

    // public sendAssetRequestTo(name:string, uri:string, callback:AssetCallback) {
    //     const http = require('http');
    //     http.request({
    //         hostname: name,
    //         port: PORT,
    //         path: uri,
    //     }, callback).end();
    // }

    private startWebSocket(settings:ConnectionSettings, onError?: Function): void {
        const {hostname} = settings;
        const port = settings.port || PORT;
        const protocol = settings.cert ? 'wss' : 'ws';
        const connectionString: string = `${protocol}://${hostname}:${port}`;
        const options = settings.cert
            ? {
                key: settings.key,
                cert: settings.cert,
                rejectUnauthorized: false,
            }
            : {};
        if (this.namedConnections[hostname]) {
            try {
                this.namedConnections[hostname].close();
            } catch (e) {
                //this error would be odd, but we'll ignore it as it would be user (dev) facing
            }
            this.namedConnections[hostname] = null;
        }
        try {
            let websocket: WebSocket | WebSocketType;
            if(NODE) {
                const WebSocket: typeof WebSocketType = require('ws');
                websocket = this.namedConnections[hostname] = new WebSocket(connectionString, options);
            }
            else {
                websocket = this.namedConnections[hostname] = new WebSocket(connectionString);
            }
            const errorCallback = (e):any => {
                if (onError){
                    onError(e);
                }
                websocket.onerror = () => { return; };
                this.onClose(hostname, 0, 'Error in websocket');
                if (NODE) {
                    (websocket as WebSocketType).removeEventListener("error", errorCallback);
                } else {
                    (websocket as WebSocket).removeEventListener("error", errorCallback);
                }
            };
            const onClose = (e) => this.onClose(hostname, e.code, e.reason);
            if (NODE) {
                (websocket as WebSocketType).addEventListener("error", errorCallback);
                (websocket as WebSocketType).addEventListener('open', () => {
                    this.onConnection(hostname, websocket as WebSocketType);
                });
                (websocket as WebSocketType).addEventListener('close', onClose);
            } else {
                (websocket as WebSocket).addEventListener("error", errorCallback);
                (websocket as WebSocket).addEventListener('open', () => {
                    this.onConnection(hostname, websocket as WebSocket);
                });
                (websocket as WebSocket).addEventListener('close', onClose);
            }
        } catch (err) {
            this.namedConnections[hostname] = null;
        }
    }

    private onConnection(hostname:string, ws: WebSocket|WebSocketType): void {
        // message from Jibo
        this.connected.emit({ robot: hostname });
        this.namedConnections[hostname] = ws;
        const listener = (message) => {
            //for comaptibility between broswer and node
            if ((message as any).data) { message = (message as any).data; }

            //wait for handshake received to
            let msg: any = JSON.parse(message.toString());
            this.message.emit({robot:hostname, data: msg});
        };
        if (NODE) {
            (ws as WebSocketType).addEventListener('message', listener);
        } else {
            (ws as WebSocket).addEventListener('message', listener);
        }
    }

    private onClose(robot: string, code:number, reason:string): void {
        if (this.namedConnections[robot]) {
            this.disconnected.emit({robot, code, reason});
            this.namedConnections[robot].close();
            this.namedConnections[robot] = null;
        }
    }
}
